import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Clock, ShoppingBag, Truck } from "lucide-react";
import { useOrder } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n, statusLabel } from "../lib/i18n";
import { chf, fmtTime, statusTone } from "../lib/format";
import { Header } from "../components/Header";

const TONE = {
  warning: "bg-hm-terracotta/15 text-hm-terracotta",
  error: "bg-hm-error/10 text-hm-error",
  neutral: "bg-hm-tertiary text-hm-muted",
  success: "bg-hm-olive/15 text-hm-olive",
  brand: "bg-hm-brandSoft text-hm-brand",
};

export default function OrderConfirmationPage() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t, tx } = useI18n();
  const { user } = useAuth();
  // Load the live order by id and poll every 4s; seed with the POST result (state) as initialData.
  const { data: order } = useOrder(id, state?.order);

  if (!order) {
    return (
      <div className="min-h-screen bg-hm-surface">
        <Header />
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
          <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-hm-line border-t-hm-brand" />
          <button type="button" data-testid="confirm-browse-menu" onClick={() => navigate("/")} className="rounded-full border-[1.5px] border-hm-lineStrong px-6 py-3 font-sans font-bold text-hm-ink">
            {t("browseMenu")}
          </button>
        </div>
      </div>
    );
  }

  const timeText = order.requested_time === "asap" ? t("asap") : order.requested_time;
  const dateText = order.requested_date ? ` · ${order.requested_date}` : "";

  return (
    <div className="min-h-screen bg-hm-surface">
      <Header />
      <div className="mx-auto w-full max-w-[640px] px-4 py-8" data-testid="order-confirmation">
        <div className="flex flex-col items-center text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-hm-olive/15 text-hm-olive"><CheckCircle2 size={36} /></span>
          <h1 className="mt-4 font-display text-4xl text-hm-ink">{t("orderReceived")}</h1>
          <p className="mt-2 font-sans text-sm text-hm-muted">{t("orderConfirmedThanks")}</p>
          <div className="mt-5 rounded-3xl bg-hm-inverse px-8 py-5 text-center text-white">
            <p className="font-sans text-xs uppercase tracking-widest text-white/70">{t("orderNumber")}</p>
            <p className="font-display text-5xl" data-testid="order-number">#{order.order_number}</p>
          </div>
          <span className={`mt-4 rounded-full px-3 py-1.5 font-sans text-sm font-extrabold ${TONE[statusTone(order.status)]}`} data-testid="order-status">
            {statusLabel(order.status, order.type, t)}
          </span>
          {order.estimated_ready_at ? (
            <p className="mt-2 font-sans text-sm font-bold text-hm-ink" data-testid="order-eta">
              {order.type === "delivery" ? t("estDelivery") : t("estReady")} {fmtTime(order.estimated_ready_at)}
              {order.estimated_minutes ? ` · ~${order.estimated_minutes} min` : ""}
            </p>
          ) : null}
        </div>

        <div className="mt-8 flex items-center gap-3 rounded-2xl border border-hm-line bg-hm-card p-4">
          {order.type === "pickup" ? <ShoppingBag size={20} className="text-hm-brand" /> : <Truck size={20} className="text-hm-brand" />}
          <div className="flex-1">
            <p className="font-display text-xl text-hm-ink" data-testid="order-type-label">{order.type === "pickup" ? t("pickupInStore").toUpperCase() : t("delivery").toUpperCase()}</p>
            {order.address ? <p className="font-sans text-[13px] text-hm-muted">{order.address.street} {order.address.number}, {order.address.npa} {order.address.city}</p> : null}
          </div>
          <div className="flex items-center gap-1.5 text-hm-muted">
            <Clock size={15} />
            <span className="font-sans text-sm font-semibold" data-testid="order-time">{timeText}{dateText}</span>
          </div>
        </div>

        {/* Items (from backend) */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-hm-line bg-hm-card">
          {order.items.map((it, idx) => (
            <div key={idx} className={`p-4 ${idx > 0 ? "border-t border-hm-line" : ""}`} data-testid={`order-item-${idx}`}>
              <div className="flex justify-between gap-2">
                <span className="font-display text-lg text-hm-ink">{it.quantity}× {tx(it.name)}{it.size ? ` · ${it.size.label}` : ""}</span>
                <span className="font-sans text-sm font-extrabold text-hm-ink">{chf(it.line_total)}</span>
              </div>
              {it.options.map((o) => <p key={o.key} className="mt-0.5 font-sans text-[13px] text-hm-olive">• {tx(o.name)}{o.price > 0 ? ` (+${chf(o.price)})` : ""}</p>)}
              {it.removed_ingredients.map((r) => <p key={r.id} className="mt-0.5 font-sans text-[13px] text-hm-error">− {t("without")} {tx(r)}</p>)}
              {it.extras.map((e) => <p key={e.extra_id} className="mt-0.5 font-sans text-[13px] text-hm-olive">+ {e.quantity > 1 ? `${e.quantity}x ` : ""}{tx(e.name)} ({chf(e.unit_price * e.quantity)})</p>)}
              {it.note ? <p className="mt-0.5 font-sans text-[13px] italic text-hm-muted">« {it.note} »</p> : null}
            </div>
          ))}
        </div>

        {order.general_note ? <p className="mt-3 font-sans text-[13px] italic text-hm-muted">« {order.general_note} »</p> : null}

        {/* Totals (backend-authoritative) */}
        <div className="mt-6 rounded-3xl bg-hm-inverse p-5 text-white">
          <Row label={t("subtotal")} value={chf(order.subtotal)} />
          <Row label={t("extrasTotal")} value={chf(order.extras_total)} />
          {order.type === "delivery" ? <Row label={t("deliveryFee")} value={order.delivery_fee === 0 ? t("free") : chf(order.delivery_fee)} /> : null}
          <div className="my-2 h-px bg-white/15" />
          <div className="flex justify-between">
            <span className="font-display text-2xl">{t("total")}</span>
            <span className="font-display text-2xl" data-testid="order-total">{chf(order.total)}</span>
          </div>
          <p className="mt-2 font-sans text-xs text-white/70" data-testid="order-payment">
            {order.type === "pickup" ? t("payAtPickup") : t("payAtDelivery")} · {order.payment_method === "cash" ? t("payCash") : t("payCard")} · {t("noOnlinePayment")}
          </p>
        </div>

        <div className="mt-8 flex gap-2.5">
          <button type="button" data-testid="confirm-browse-menu" onClick={() => navigate("/")} className="flex-1 rounded-full border-[1.5px] border-hm-lineStrong py-3 font-sans font-bold text-hm-ink transition-colors hover:bg-hm-tertiary">
            {t("browseMenu")}
          </button>
          {user ? (
            <button type="button" data-testid="confirm-view-orders" onClick={() => navigate("/account")} className="flex-1 rounded-full bg-hm-brand py-3 font-sans font-extrabold text-white">
              {t("trackOrder")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="font-sans text-sm text-white/80">{label}</span>
      <span className="font-sans text-sm font-semibold text-white">{value}</span>
    </div>
  );
}
